import { useTranslation } from 'react-i18next'
import { Departure, WidgetTranslations } from '../data.ts'
import { DEFAULT_LANGUAGE } from '../i18n.ts'
import { ChangeEvent, PropsWithChildren, useCallback, useContext, useId, useMemo, useState } from 'react'
import { Filters, SortKey, useFilters } from '../context/filters.tsx'
import { applyFilterConfiguration, SORT_KEYS } from '../lib/filters.ts'
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover.tsx'
import { ChevronDown } from '../icons/chevrons.tsx'
import { parseISO } from 'date-fns'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger
} from './ui/dialog.tsx'
import { MainContext } from '../context/main.tsx'
import { FilterIcon } from '../icons/filter.tsx'
import { CloseIcon } from '../icons/dialog.tsx'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion.tsx'
import { cn } from '@/lib/cn.ts'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select.tsx'

interface YearFilterProps {
  year?: number
  years: number[]
  onYearChange: (year: number) => void
}

interface FilterBarProps extends YearFilterProps {
  departures: Departure[]
  translations: WidgetTranslations
}

interface FilterFormProps extends YearFilterProps {
  departures: Departure[]
  ships: Array<[string, string]>
  guidances: Array<[string, string]>
  harbours: Array<[string, string]>
  months: Array<[string, string]>
  availabilities: Array<[string, string]>
}

interface FilterProps<T extends keyof Filters> {
  departures: Departure[]
  label: string
  filterName: keyof Filters
  filterValues: Array<[Filters[T][number], string]>
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

interface FilterOptionProps<T extends keyof Filters> {
  departures: Departure[]
  label: string
  filterName: T
  filterValue: Filters[T][number]
}

function DropdownFilterOption<T extends keyof Filters> ({
  departures,
  label,
  filterName,
  filterValue
}: FilterOptionProps<T>): JSX.Element {
  const id = useId()
  const { t } = useTranslation('widget')
  const { filters, sort, setFilter } = useFilters()
  const newFilters = useMemo((): Filters => ({
    ...filters,
    [filterName]: [filterValue]
  } satisfies Filters), [filters, filterName, filterValue])
  const remaining = useMemo(() => applyFilterConfiguration(departures, newFilters, sort), [departures, newFilters, sort])
  const currentValue = filters[filterName]
  const changeValue = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.currentTarget.checked) {
      if (!currentValue.includes(filterValue)) {
        setFilter(filterName, [...currentValue, filterValue])
      }
    } else {
      setFilter(filterName, currentValue.filter(value => value !== filterValue))
    }
  }, [currentValue, filterName, filterValue, setFilter])
  return (
    <label
      className='grid grid-cols-subgrid col-span-3 items-center gap-y-3 cursor-pointer select-none has-disabled:opacity-40 has-disabled:cursor-not-allowed'
      htmlFor={id}
    >
      <div><input
        type='checkbox' className='accent-brand-primary' checked={filters[filterName].includes(filterValue)}
        id={id}
        onChange={changeValue}
        disabled={remaining.length === 0}
           />
      </div>
      <div>{label}</div>
      <div className='flex justify-end'>
        <span
          className='hidden @desktop:inline bg-brand-primary-lighter border border-brand-primary-light rounded-xs px-2 py-1'
        >{remaining.length}
        </span>
        <span
          className='@desktop:hidden bg-brand-primary-lighter border border-brand-primary-light rounded-xs px-2 py-0.5'
        >{t('x_tours', { count: remaining.length })}
        </span>
      </div>
    </label>
  )
}

function DesktopDropdownFilter<T extends keyof Filters> ({
  departures,
  label,
  filterName,
  filterValues,
  open = false,
  onOpenChange
}: FilterProps<T>): JSX.Element {
  const { filters, sort, setFilter } = useFilters()
  const filteredDepartures = useMemo(() => applyFilterConfiguration(departures, filters, sort), [departures, filters, sort])
  const { t } = useTranslation('widget')
  const { mainRef } = useContext(MainContext)

  const clearValue = useCallback(() => {
    setFilter(filterName, [])
    onOpenChange?.(false)
  }, [filterName, setFilter, onOpenChange])
  return (
    <Popover open={open} onOpenChange={(isOpen) => onOpenChange?.(isOpen)}>
      <PopoverTrigger
        className='inline-flex gap-2 border rounded-sm px-2 py-1 border-base-3 items-center cursor-pointer'
      >
        {label}
        {filters[filterName].length > 0 && (
          <span
            className='bg-brand-primary-light rounded-full font-bold text-body-xs flex items-center px-2'
          >{filters[filterName].length}
          </span>
        )}
        <ChevronDown className='size-2' />
      </PopoverTrigger>
      <PopoverContent
        className='divide-y divide-base-3 bg-white p-0' align='start' container={mainRef}
        side='bottom'
      >
        <div className='grid grid-cols-[auto_1fr_auto] gap-x-2 gap-y-3 p-4'>
          {filterValues.map(([optionValue, optionLabel]) => (
            <DropdownFilterOption
              departures={departures} label={optionLabel} filterName={filterName}
              filterValue={optionValue} key={optionValue}
            />
          ))}
        </div>
        <div className='flex justify-between p-4 text-body-xs'>
          <button
            type='button' className='font-bold underline cursor-pointer'
            onClick={clearValue}
          >{t('clear')}
          </button>
          <button
            type='button'
            className='bg-brand-secondary rounded-md font-bold shadow-button px-4 py-1 cursor-pointer'
            onClick={() => onOpenChange?.(false)}
          >{t('show_x_tours', { count: filteredDepartures.length })}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

function YearFilter ({ year, years, onYearChange, className }: YearFilterProps & {
  className?: string
}): JSX.Element {
  const { t } = useTranslation('widget')
  const { mainRef } = useContext(MainContext)
  const items = years.map(optionYear => ({ label: optionYear, value: optionYear }))
  const onChange = useCallback((newValue: number | null) => {
    if (newValue != null) {
      onYearChange(newValue)
    }
  }, [onYearChange])
  return (
    <>
      <Select items={items} value={year} onValueChange={onChange}>
        <SelectTrigger className={cn('text-brand-primary', className)}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent className='text-brand-primary' container={mainRef}>
          <SelectGroup>
            <SelectLabel>{t('year')}</SelectLabel>
            {items.map(item => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </>
  )
}

function SortSelect ({
  id,
  sort,
  setSort
}: {
  id: string
  sort: SortKey
  setSort: (sort: SortKey) => void
}): JSX.Element {
  const { mainRef } = useContext(MainContext)
  const { t } = useTranslation('widget')
  const items = useMemo(() => SORT_KEYS.map(sortKey => ({
    label: t(sortKey, { keyPrefix: 'sort_keys' }),
    value: sortKey
  })), [t])
  const onChange = useCallback((value: SortKey | null) => {
    if (value != null) {
      setSort(value)
    }
  }, [setSort])
  return (
    <>
      <Select items={items} value={sort} onValueChange={onChange}>
        <SelectTrigger className='border-base-3' id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent container={mainRef} align='end' className='ring-base-3'>
          <SelectGroup className='border-base-3'>
            <SelectLabel>{t('sort_by')}</SelectLabel>
            {items.map(item => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </>
  )
}

function DesktopFilters ({
  departures,
  ships,
  guidances,
  harbours,
  months,
  availabilities,
  years,
  year,
  onYearChange
}: FilterFormProps): JSX.Element {
  const { t } = useTranslation('widget')
  const { clearFilters, filters, sort, setSort } = useFilters()
  const filtersSet = useMemo(() => Object.values(filters).filter(value => value.length > 0).length > 0, [filters])
  const sortId = useId()
  const [openFilter, setOpenFilter] = useState<keyof Filters | null>(null)
  const handleOpenChange = useCallback((filterName: keyof Filters, isOpen: boolean) => {
    setOpenFilter(isOpen ? filterName : null)
  }, [])
  return (
    <div className='hidden @desktop:flex justify-between max-w-(--site-max-width-no-padding) mx-auto'>
      <div className='flex gap-4'>
        {year != null && (
          <>
            <YearFilter years={years} onYearChange={onYearChange} year={year} />
            <span className='border-r border-r-base-3' />
          </>
        )}
        <DesktopDropdownFilter
          departures={departures} label={t('date')} filterName='date'
          filterValues={months}
          open={openFilter === 'date'} onOpenChange={(isOpen) => handleOpenChange('date', isOpen)}
        />
        <DesktopDropdownFilter
          departures={departures} label={t('departure')} filterName='departure'
          filterValues={harbours}
          open={openFilter === 'departure'} onOpenChange={(isOpen) => handleOpenChange('departure', isOpen)}
        />
        <DesktopDropdownFilter
          departures={departures} label={t('ship')} filterName='ship' filterValues={ships}
          open={openFilter === 'ship'} onOpenChange={(isOpen) => handleOpenChange('ship', isOpen)}
        />
        <DesktopDropdownFilter
          departures={departures} label={t('guidance')} filterName='guidance'
          filterValues={guidances}
          open={openFilter === 'guidance'} onOpenChange={(isOpen) => handleOpenChange('guidance', isOpen)}
        />
        <DesktopDropdownFilter
          departures={departures} label={t('availability')} filterName='availability'
          filterValues={availabilities}
          open={openFilter === 'availability'} onOpenChange={(isOpen) => handleOpenChange('availability', isOpen)}
        />
        {filtersSet && (
          <button
            type='button' className='font-bold underline cursor-pointer'
            onClick={clearFilters}
          >{t('clear_filter', 'Clear filters')}
          </button>
        )}
      </div>
      <div className='flex items-center gap-2'>
        <label htmlFor={sortId}>{t('sort_by')}</label>
        <SortSelect sort={sort} setSort={setSort} id={sortId} />
      </div>
    </div>
  )
}

function MobileAccordionSort (): JSX.Element {
  const { t } = useTranslation('widget')
  const { sort, setSort } = useFilters()
  return (
    <AccordionItem value='sort' className='p-4 border-b border-b-base-2'>
      <AccordionTrigger className='py-0'>
        <span className='inline-flex items-center gap-2'>
          {t('sort_by')}
        </span>
      </AccordionTrigger>
      <AccordionContent className='grid gap-x-2 gap-y-2 pt-4 pb-0'>
        <div className='flex'>
          <select
            value={sort} onChange={e => setSort(e.currentTarget.value as SortKey)}
            className='w-full grow gap-2 border rounded-sm px-2 py-1.5 border-base-3 items-center cursor-pointer'
          >
            {SORT_KEYS.map(sortKey => (
              <option key={sortKey} value={sortKey}>{t(sortKey, { keyPrefix: 'sort_keys' })}</option>
            ))}
          </select>
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}

function MobileAccordionFilter<T extends keyof Filters> ({
  departures,
  label,
  filterName,
  filterValues,
  onClose
}: FilterProps<T> & {
  onClose: (name: keyof Filters) => void
}): JSX.Element {
  const { filters, setFilter } = useFilters()
  const { t } = useTranslation('widget')
  const activeFilters = filters[filterName].length

  const clearValue = useCallback(() => {
    setFilter(filterName, [])
  }, [filterName, setFilter])
  const close = useCallback(() => onClose(filterName), [filterName, onClose])

  return (
    <AccordionItem value={filterName} className='p-4 border-b border-b-base-2'>
      <AccordionTrigger className='py-0'>
        <span className='inline-flex items-center gap-2'>
          {label}
          {activeFilters > 0 && (
            <span
              className='bg-brand-primary-light rounded-full font-bold text-body-xs flex items-center px-2'
            >{activeFilters}
            </span>
          )}
        </span>
      </AccordionTrigger>
      <AccordionContent className='grid grid-cols-[auto_1fr_auto] gap-x-2 gap-y-2 pt-4 pb-0'>
        {filterValues.map(([optionValue, optionLabel]) => (
          <DropdownFilterOption
            key={optionValue}
            departures={departures} label={optionLabel} filterName={filterName}
            filterValue={optionValue}
          />
        ))}
        <div className='col-span-3 flex justify-between items-center mt-2'>
          <button
            type='button' className='font-bold underline cursor-pointer'
            onClick={clearValue}
          >{t('clear', 'Clear')}
          </button>
          <button
            type='button'
            className='bg-brand-primary text-white rounded-md font-bold shadow-button px-4 py-1 cursor-pointer'
            onClick={close}
          >{t('apply', 'Apply')}
          </button>
        </div>
      </AccordionContent>
    </AccordionItem>
  )
}

function MobileFilters ({
  departures,
  ships,
  guidances,
  harbours,
  months,
  availabilities,
  years,
  year,
  onYearChange
}: FilterFormProps): JSX.Element {
  const { t } = useTranslation('widget')
  const { filters, sort } = useFilters()
  const { mainRef } = useContext(MainContext)
  const [openFilters, setOpenFilters] = useState<Array<keyof Filters>>([])
  const closeFilter = useCallback((name: keyof Filters) => {
    setOpenFilters(filters => filters.filter(filter => filter !== name))
  }, [])
  const activeFilters = useMemo(() => [filters.date, filters.ship, filters.availability, filters.guidance, filters.departure].filter(filter => filter.length > 0).length, [filters])
  const filteredDepartures = useMemo(() => applyFilterConfiguration(departures, filters, sort), [departures, filters, sort])
  return (
    <div className='grid @desktop:hidden'>
      <Dialog>
        <div className='flex gap-2 w-(--site-max-width-no-padding) mx-auto'>
          {year != null && (
            <YearFilter years={years} onYearChange={onYearChange} year={year} />
          )}
          <DialogTrigger
            className='grow flex justify-center gap-3 items-center rounded-md text-brand-primary border-2 border-brand-primary py-1 font-bold'
          >
            <FilterIcon className='size-4' />
            {t('filter_results', 'Filter results')}
            {activeFilters > 0 && (
              <span
                className='bg-brand-primary-light rounded-full font-bold text-body-xs flex items-center px-2'
              >{activeFilters}
              </span>
            )}
          </DialogTrigger>
        </div>
        <DialogContent
          container={mainRef} showCloseButton={false}
          className='[--dialog-margin:1rem] rounded-lg h-[calc(100dvh-var(--dialog-margin,2rem))] flex flex-col'
        >
          <DialogHeader
            className='bg-brand-primary text-white p-4 rounded-t-lg flex justify-between items-center flex-row'
          >
            <div className='flex gap-4 justify-start items-center'>
              <FilterIcon className='size-4' />
              {t('filter_tours', 'Filter tours')}
              {activeFilters > 0 && (
                <span
                  className='bg-brand-primary-light text-black rounded-full font-bold text-body-xs flex items-center px-2'
                >{activeFilters}
                </span>
              )}
            </div>
            <DialogClose
              render={
                <button type='button' title={t('close')} className='cursor-pointer'>
                  <CloseIcon />
                </button>
              }
            />
          </DialogHeader>
          <DialogBody className='overflow-y-auto p-0 grow'>
            <Accordion multiple value={openFilters} onValueChange={setOpenFilters}>
              <MobileAccordionSort />
              <MobileAccordionFilter
                departures={departures} label={t('date')} filterName='date'
                filterValues={months}
                onClose={closeFilter}
              />
              <MobileAccordionFilter
                departures={departures} label={t('departure')} filterName='departure'
                filterValues={harbours}
                onClose={closeFilter}
              />
              <MobileAccordionFilter
                departures={departures} label={t('ship')} filterName='ship' filterValues={ships}
                onClose={closeFilter}
              />
              <MobileAccordionFilter
                departures={departures} label={t('guidance')} filterName='guidance'
                filterValues={guidances}
                onClose={closeFilter}
              />
              <MobileAccordionFilter
                departures={departures} label={t('availability')} filterName='availability'
                filterValues={availabilities}
                onClose={closeFilter}
              />
            </Accordion>
          </DialogBody>
          <DialogFooter className='m-0 p-4 border-t-0 rounded-b-lg'>
            <DialogClose render={
              <button
                type='button'
                className='bg-brand-secondary grow rounded-md font-bold shadow-button px-4 py-3 cursor-pointer active:translate-y-1'
              >
                {t('show_x_tours', { count: filteredDepartures.length })}
              </button>
            }
            />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export function StickyBar ({ className, children }: PropsWithChildren<{ className?: string }>): JSX.Element {
  const [stuck, setStuck] = useState(false)
  const sentinelRef = useCallback((sentinel: HTMLDivElement | null) => {
    if (sentinel == null) return
    const observer = new IntersectionObserver(
      ([entry]) => setStuck(!entry.isIntersecting),
      { rootMargin: '-10px 0px 0px 0px' }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <div
        ref={sentinelRef} className='relative -top-(--keeptrack-scroll-padding-top) h-0 w-full'
        aria-hidden='true'
      />
      <div
        data-stuck={stuck || undefined}
        className={cn('sticky top-[calc(var(--keeptrack-scroll-padding-top,134px)-1px)] z-2 data-stuck:bg-white py-4 -my-4', className)}
      >{children}
      </div>
    </>
  )
}

export function FilterBar ({ departures, translations, year, onYearChange, years }: FilterBarProps): JSX.Element {
  const { i18n, t } = useTranslation('widget')

  const shipOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const departure of departures) {
      map.set(departure.ship.id, departure.ship.name)
    }
    for (const ship of translations.ships) {
      if (map.has(ship.id)) {
        const translation = ship.translations.find(t => t.language === i18n.language) ?? ship.translations.find(t => t.language === DEFAULT_LANGUAGE)
        if (translation != null) {
          map.set(ship.id, translation.name)
        }
      }
    }
    return [...map.entries()].toSorted((a, b) => a[1].localeCompare(b[1], i18n.language))
  }, [i18n.language, translations, departures])

  const guidanceOptions = useMemo((): Array<[string, string]> => {
    return ['Guided', 'Semi-Guided', 'Independent'].map(guidance => [guidance, t(guidance, { keyPrefix: 'guidance_types' })])
  }, [t])

  const harbourOptions = useMemo(() => {
    const map = new Map<string, string>()
    for (const departure of departures) {
      map.set(departure.port_of_departure.id, departure.port_of_departure.name)
    }
    for (const harbour of translations.harbours) {
      if (map.has(harbour.id)) {
        const translation = harbour.translations.find(t => t.language === i18n.language) ?? harbour.translations.find(t => t.language === DEFAULT_LANGUAGE)
        if (translation != null) {
          map.set(harbour.id, translation.name)
        }
      }
    }
    return [...map.entries()].toSorted((a, b) => a[1].localeCompare(b[1], i18n.language))
  }, [i18n.language, translations, departures])

  const monthOptions = useMemo((): Array<[string, string]> => {
    const set = new Set<string>()
    for (const departure of departures) {
      set.add(departure.start_date.substring(0, 7))
    }

    const months = [...set].toSorted()
    return months.map(month => {
      return [
        month,
        t('format.month', { value: parseISO(`${month}-01`) })
      ]
    })
  }, [t, departures])

  const availabilityOptions = useMemo((): Array<[string, string]> => {
    return ['available', 'on_request'].map(availability => [
      availability,
      t(availability, { keyPrefix: 'departure_statuses' })
    ])
  }, [t])

  return (
    <StickyBar className='
    data-stuck:p-4 left-0 right-0
    data-stuck:shadow
    data-stuck:-mx-[calc((100vw-var(--main-width)-var(--scrollbar-width))/2)]
    peer
    '
    >
      <DesktopFilters
        ships={shipOptions} guidances={guidanceOptions} harbours={harbourOptions} months={monthOptions}
        availabilities={availabilityOptions}
        departures={departures}
        years={years} year={year} onYearChange={onYearChange}
      />
      <MobileFilters
        ships={shipOptions} guidances={guidanceOptions} harbours={harbourOptions} months={monthOptions}
        availabilities={availabilityOptions}
        departures={departures}
        years={years} year={year} onYearChange={onYearChange}
      />
    </StickyBar>
  )
}
